'use client'
/**
 * Design System - Text Avatar Subcomponent
 * https://mercury.design/system?p=avatar
 * https://www.figma.com/file/S7Q78jkg0O92CodjYielQO/%F0%9F%92%BB-Design-System---Web?node-id=133%3A4762
 */
import classnames from 'classnames'
import * as R from 'ramda'
import React from 'react'
import {getInitials} from '~/design-system/utils/getInitials'
import styles from '~/mercuryWebCompat/design-system/MWDSAvatar/styles.module.css'
import {removeDiacritics} from '~/utils/removeDiacritics'
import BaseAvatar, {type BaseAvatarExternalProps} from '../BaseAvatar'

const validCharacters = new Set(
  R.splitEvery(1, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789')
)

const stringToClassName = (s: string): string => {
  const firstLetter = removeDiacritics(s[0]).toUpperCase()
  if (validCharacters.has(firstLetter)) {
    return `starts-with-${firstLetter}`
  } else {
    return 'backup-color'
  }
}

export type AvatarTextProps = {
  /**
   * Name of the person represented by this Avatar.
   * The first and last initials will be extracted
   */
  name: string | {firstName: string; lastName: string}

  avatarSrc?: never

  /**
   * When set, disables the default background color. You can then set a custom background color if needed.
   */
  noDefaultBackgroundColor?: boolean
} & BaseAvatarExternalProps

/**
 * A TextAvatar is an avatar that displays initials. Currently, these are most commonly
 * used to represent individuals and non-bank businesses. One, or potentially two, initials
 * will be extracted from the supplied `name`, and displayed in the Avatar.
 */
const TextAvatar: React.FC<AvatarTextProps> = ({
  name,
  noDefaultBackgroundColor,
  ...divProps
}: AvatarTextProps) => {
  const stringName =
    typeof name === 'string' ? name : `${name.firstName} ${name.lastName}`
  const initials = getInitials(stringName)
  const firstLetter = initials[0]
  const secondLetter = initials[1]

  return (
    <BaseAvatar
      {...divProps}
      className={classnames(
        !noDefaultBackgroundColor && styles[stringToClassName(stringName)],
        styles.textAvatar,
        divProps.className
      )}
      aria-hidden="true"
    >
      {divHovered => (
        <>
          <span className={secondLetter ? '' : styles['last-letter']}>
            {firstLetter}
          </span>
          {secondLetter && (
            <span className={styles['last-letter']}>{secondLetter}</span>
          )}
        </>
      )}
    </BaseAvatar>
  )
}

export default TextAvatar
